<template>
  <div class="container">
    <div class="print-container" ref="printContainer">
      <!-- Header -->
      <header class="header">
        <div class="logo">
          <img
              src="https://cdn01.immigration.gov.np/public/images/nepal_sarkar.png"
              alt="Nepal Logo"
          />
        </div>
        <div class="header-text">
          <h2>Department of Immigration</h2>
          <p>Kalikathan, Dilli Bazar, Kathmandu</p>
          <p>01-4529660</p>
          <p><a href="mailto:info@immigration.gov.np" target="_blank">info@immigration.gov.np</a></p>
        </div>
      </header>

      <h2 class="form-title">Tourist Visa Application Form</h2>

      <!-- Form Data Table -->
      <table class="info-table">
        <tbody>
        <tr>
          <td>Submission Id:</td>
          <td><strong>{{ submissionNo }}</strong></td>
        </tr>
        <tr>
          <td>Full Name</td>
          <td>{{ travellerName }}</td>
        </tr>
        <tr>
          <td>Date of Birth</td>
          <td>{{ dateOfBirth }}</td>
        </tr>
        <tr>
          <td>Country</td>
          <td>{{ country }}</td>
        </tr>
        <tr>
          <td>Passport Number</td>
          <td>{{ passportNumber }}</td>
        </tr>
        <tr>
          <td>Passport Validity Date</td>
          <td>{{ passportValidity }}</td>
        </tr>
        <tr>
          <td>Applied Date & Time</td>
          <td>{{ appliedDate }}</td>
        </tr>
        <tr>
          <td>Visa Duration(Days)</td>
          <td>{{ visaDuration }}</td>
        </tr>
        <tr>
          <td>Applied Office</td>
          <td>{{ appliedOffice }}</td>
        </tr>
        <tr>
          <td>Total cost($)</td>
          <td>{{ totalCost }}</td>
        </tr>
        <tr>
          <td>Voucher number</td>
          <td>{{ voucherNumber }}</td>
        </tr>
        </tbody>
      </table>

      <!-- Instructions -->
      <ul class="instructions">
        <li>
          <strong>Save or Print Your Submission Number:</strong> Make sure to save or print your submission number for future reference.
        </li>
        <li>
          <strong>Non-Refundable Payments:</strong> Please note that multiple payments for the same service (Revenue Voucher) for Government of Nepal are non-refundable.
        </li>
        <li>
          <strong>Payment Options:</strong> You can make the payment online or via Card or visit a bank counter at Immigration to complete the payment.
        </li>
        <li>
          <strong>Card Payment Issues:</strong> If you experience any issues with card payments, please email your submission details to
          <a href="mailto:support@nchl.com.np" target="_blank">support@nchl.com.np</a>.
        </li>
        <li>
          <strong>Check Payment Status:</strong> You can check the status of your payment using the following link:
          <a href="https://nepaliport-new.immigration.gov.np/card-payment" target="_blank">
            https://nepaliport-new.immigration.gov.np/card-payment
          </a>
        </li>
      </ul>
    </div>

    <!-- Action Buttons -->
    <div class="buttons">
      <v-btn color="primary" @click="handlePrint" :loading="printLoading">
        PRINT
      </v-btn>
      <v-btn color="grey darken-2" dark @click="handleDownload" :loading="downloadLoading">
        DOWNLOAD
      </v-btn>
      <v-btn color="info" @click="showCardDialog = true" :loading="cardPaymentLoading">
        CARD PAYMENT
      </v-btn>
      <v-btn color="warning" @click="handlePayOnline" :loading="onlinePaymentLoading">
        PAY ONLINE
      </v-btn>
      <v-btn @click="redirectToPayment" style="background-color: green;color:#fff;">
        Check Payment
      </v-btn>
      <v-btn color="success" @click="handleDone">
        DONE
      </v-btn>
      <v-btn color="error" @click="handleClose">
        CLOSE
      </v-btn>
    </div>

    <!-- Payment iframe -->
    <div v-if="paymentUrl" class="iframe-container">
      <v-btn
          icon
          color="error"
          class="close-button"
          @click="closeIframe"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <iframe
          :src="paymentUrl"
          width="100%"
          height="100%"
          frameborder="0"
          title="Payment Gateway"
          class="payment-iframe"
      ></iframe>
    </div>

    <!-- Terms & Conditions Dialog -->
    <v-dialog
        v-model="showCardDialog"
        persistent
        max-width="600px"
    >
      <v-card>
        <v-card-title class="blue white--text">
          <v-icon left color="white">mdi-file-document</v-icon>
          <span class="text-h6">Terms & Conditions</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="showCardDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <p class="mb-3 black--text">
            <strong>Save or Print Your Submission Number:</strong> Make sure to save or print your submission number for future reference.
          </p>
          <p class="mb-3 black--text">
            <strong>Non-Refundable Payments:</strong> Please note that multiple payments for the same service (Revenue Voucher) for Government of Nepal are non-refundable.
          </p>
          <p class="mb-3 black--text">
            <strong>Payment Options:</strong> You can make the payment online or via Card or visit a bank counter at Immigration to complete the payment.
          </p>
          <p class="mb-3 black--text">
            <strong>Card Payment Issues:</strong> If you experience any issues with card payments, please email your submission details to
            <a href="mailto:support@nchl.com.np" target="_blank">support@nchl.com.np</a>.
          </p>
          <p class="mb-3 black--text">
            <strong>Check Payment Status:</strong> You can check the status of your payment using the following link:
            <a href="https://nepaliport-new.immigration.gov.np/card-payment" target="_blank">
              https://nepaliport-new.immigration.gov.np/card-payment
            </a>
          </p>

          <v-checkbox
              v-model="acceptedTerms"
              label="I have read and agree to the terms above"
              color="primary"
              class="mt-4"
          ></v-checkbox>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
              color="primary"
              :disabled="!acceptedTerms"
              :loading="cardPaymentLoading"
              @click="handleCardPayment"
          >
            <v-icon left>mdi-check-circle</v-icon>
            Proceed
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Http from "axios";
import {API_URL,VERSION} from "../config/config";


export default {
  name: 'TouristVisaForm',

  props: {
    visit: {
      type: Object,
      default: () => ({})
    },
    unapprovedTravellerMessage: {
      type: String,
      default: ''
    },
    unapprovedTravellerMalanikaSession: {
      type: String,
      default: ''
    },
    submitUnapprovedTraveller: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      paymentUrl: null,
      showCardDialog: false,
      acceptedTerms: false,
      cardPaymentLoading: false,
      onlinePaymentLoading: false,
      printLoading: false,
      downloadLoading: false
    };
  },

  computed: {
    malanikaSessionId() {
      return this.unapprovedTravellerMalanikaSession || '';
    },

    submissionNo() {
      return (this.visit && this.visit.submission_no) || '';
    },

    travellerName() {
      return (this.visit && this.visit.traveller && this.visit.traveller.full_name) || '';
    },

    dateOfBirth() {
      return (this.visit && this.visit.traveller && this.visit.traveller.dob) || '';
    },

    country() {
      return (this.visit && this.visit.traveller && this.visit.traveller.country_name) || '';
    },

    passportNumber() {
      return (this.visit && this.visit.passport_document && this.visit.passport_document.document_number) || '';
    },

    passportValidity() {
      return (this.visit && this.visit.passport_document && this.visit.passport_document.validity) || '';
    },

    appliedDate() {
      return (this.visit && this.visit.created_at) || '';
    },

    visaDuration() {
      return (this.visit && this.visit.track && this.visit.track[0] && this.visit.track[0].days) || '';
    },

    appliedOffice() {
      return (this.visit && this.visit.office) || 'Tribhuvan International Airport (TIA)';
    },

    totalCost() {
      const amount = (this.visit && this.visit.track && this.visit.track[0] && this.visit.track[0].amount) || '';
      const voucherAmount = (this.visit && this.visit.extra && this.visit.extra.voucher_amount) || '';
      return voucherAmount ? `${amount} (${voucherAmount})` : amount;
    },

    voucherNumber() {
      return (this.visit && this.visit.voucher_number) || '';
    },
    authToken() {
      return (this.visit && this.visit.extra && this.visit.extra.token) || '';
    },

    voucherToken() {
      return (this.visit && this.visit.extra && this.visit.extra.voucher_token) || '';
    }
  },

  methods: {
    handlePrint() {
      this.printLoading = true;
      try {
        window.print();
      } catch (error) {
        console.error('Print error:', error);
        this.showError('Failed to print document');
      } finally {
        this.printLoading = false;
      }
    },

    redirectToPayment() {
      const submissionNo = this.submissionNo;
      const url = `https://nepaliport-new.immigration.gov.np/card-payment?submission_no=${encodeURIComponent(submissionNo)}`;
      window.open(url, "_blank");
    },

    async handleDownload() {
      this.downloadLoading = true;

      try {
        const html2canvas = (await import('html2canvas')).default;
        const jsPDF = (await import('jspdf')).default;

        const element = this.$refs.printContainer;

        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false
        });

        const imgData = canvas.toDataURL('image/png');

        const pdf = new jsPDF('p', 'mm', 'a4');

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pdfWidth;
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;

        // First page
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;

        // Add more pages if content is longer than A4
        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pdfHeight;
        }

        // Download the PDF
        pdf.save(`Tourist_Visa_${this.submissionNo || 'Application'}.pdf`);

        this.showSuccess('PDF downloaded successfully!');
      } catch (error) {
        console.error('PDF Error:', error);
        this.showError('Failed to generate PDF');
      } finally {
        this.downloadLoading = false;
      }
    },

    handleDone() {
      this.$emit('done');
    },

    handleClose() {
      this.$emit('close');
    },

    async handleCardPayment() {
      if (!this.acceptedTerms) {
        this.showWarning('Please accept the terms and conditions');
        return;
      }
      this.cardPaymentLoading = true;
      try {
        this.showCardDialog = false;

        const response = await Http.post(
            `${API_URL}${VERSION}/iPayment-public/${this.voucherNumber}`,
            {},
            {
              headers: {
                Authorization: `${this.authToken}`
              }
            }
        );

        if (response.data.success) {
          this.paymentUrl = response.data.data.data.formData.url;
          this.showSuccess('Redirecting to payment gateway');
        } else {
          const errorMsg = response.data.data.message || 'Failed to initiate payment. Please try again later.';
          this.showError(errorMsg);
        }
      } catch (error) {
        console.error('Card payment error:', error);
        this.showError('Failed to initiate payment. Please try again later.');
      } finally {
        this.cardPaymentLoading = false;
      }
    },

    async handlePayOnline() {
      this.onlinePaymentLoading = true;
      try {
        await this.payThroughFCGO(this.visit);
      } catch (error) {
        console.error('Online payment error:', error);
        this.showError('Failed to initiate online payment');
      } finally {
        this.onlinePaymentLoading = false;
      }
    },

    closeIframe() {
      this.paymentUrl = null;
    },

    payThroughFCGO(unapprovedVisaData) {
      const params = {
        code: unapprovedVisaData.voucher_number || '',
        token: this.voucherToken,
        sessionId: this.malanikaSessionId,
        api_url: (unapprovedVisaData && unapprovedVisaData.malanika_public_gateway) ||
            'https://revenue.fcgo.gov.np/gateway/start-payment-token'
      };

      this.postToFCGO(params);
    },

    postToFCGO(data) {
      const form = this.createFcgoForm(data);
      const targetUrl = data.api_url;
      const width = 1000;
      const height = 600;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;

      const newWindow = window.open('', '_blank', features);

      if (newWindow) {
        try {
          newWindow.focus();
          const newDoc = newWindow.document;
          newDoc.body.appendChild(form);
          form.submit();
          newDoc.body.removeChild(form);
        } catch (error) {
          console.error('FCGO form submission error:', error);
          this.showError('Failed to open payment form');
        }
      } else {
        this.showError('Please allow pop-ups to open the payment form');
      }
    },

    createFcgoForm(data) {
      const form = document.createElement('form');
      form.setAttribute('method', 'POST');
      form.setAttribute('action', data.api_url);

      const params = {
        code: data.code,
        token: data.token,
        sessionId: this.malanikaSessionId,
        lang: 'np'
      };

      Object.entries(params).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.setAttribute('type', 'hidden');
        input.setAttribute('name', key);
        input.setAttribute('value', value || '');
        form.appendChild(input);
      });

      return form;
    },

    // Notification helpers
    showSuccess(message) {
      this.$store.commit('Notification/showSuccess', message);
    },

    showError(message) {
      this.$store.commit('Notification/showError', message);
    },

    showWarning(message) {
      this.$store.commit('Notification/showWarning', message);
    }
  }
};
</script>

<style scoped>
.container {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
}

.print-container {
  background: white;
  padding: 20px;
  border-radius: 8px;
}

.header {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 15px;
  margin-bottom: 20px;
}

.logo img {
  width: 100px;
  height: 90px;
  margin-right: 20px;
  object-fit: contain;
}

.header-text h1 {
  margin: 0;
  font-size: 24px;
  color: #000;
  font-weight: 600;
}

.header-text p {
  margin: 4px 0;
  font-size: 14px;
  color: #111;
}

.form-title {
  text-align: left;
  font-size: 20px;
  margin: 20px 0;
  color: #111;
  font-weight: 600;
}

.info-table {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
  background: white;
}

.info-table td {
  padding: 12px 15px;
  border: 1px solid #000;
  font-size: 14px;
}

.info-table td:first-child {
  font-weight: 600;
  width: 35%;
  color: #000;
}

.info-table td:last-child {
  color: #000;
}

.instructions {
  padding-left: 20px;
  margin: 30px 0;
  line-height: 1.8;
  color: #000;
}

.instructions li {
  margin-bottom: 16px;
  font-size: 14px;
}

.instructions a {
  color: #1976d2;
  text-decoration: none;
  transition: color 0.2s;
}

.instructions a:hover {
  color: #1565c0;
  text-decoration: underline;
}

.buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin: 40px 0;
  padding: 20px 0;
}

.iframe-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
}

.payment-iframe {
  width: 90%;
  height: 90%;
  background: white;
  border-radius: 8px;
}

.close-button {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 10000;
}

/* Print styles */
@media print {
  @page {
    size: A4;
    margin: 15mm;
  }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .buttons,
  .iframe-container {
    display: none !important;
  }

  .container {
    box-shadow: none;
    border: none;
    max-width: 100%;
    margin: 0;
    padding: 0;
  }

  .print-container {
    page-break-inside: avoid;
    box-shadow: none;
    padding: 0;
  }

  .header {
    padding: 8px 0;
    margin-bottom: 10px;
  }

  .logo img {
    width: 70px;
    height: 65px;
    margin-right: 15px;
  }

  .header-text h1 {
    font-size: 18px;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 20px;
    padding-bottom: 10px;
  }

  .header-text p {
    margin: 3px 0;
    font-size: 11pt;
    text-align: left !important;
    line-height: 1.4;
  }
  .form-title {
    font-size: 16px;
    margin: 10px 0;
  }

  .info-table {
    margin: 10px 0;
    font-size: 12px;
  }

  .info-table td {
    padding: 6px 10px;
  }

  .instructions {
    margin: 15px 0;
    padding-left: 20px;
    font-size: 11px;
    line-height: 1.5;
  }

  .instructions li {
    margin-bottom: 8px;
  }
}

/* Responsive styles */
@media (max-width: 768px) {
  .container {
    padding: 10px;
  }

  .header {
    flex-direction: column;
    text-align: center;
  }

  .logo img {
    margin: 0 0 10px 0;
  }

  .buttons {
    flex-direction: column;
  }

  .info-table td {
    padding: 8px 10px;
    font-size: 13px;
  }

  .info-table td:first-child {
    width: 40%;
  }
}
@media print {
  .header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 8px 0;
    margin-bottom: 10px;
    flex-direction: row; /* ensure row layout */
  }

  .logo img {
    width: 80px;
    height: auto;
    margin-right: 20px;
  }

  .header-text {
    display: block;
  }

  .header-text h2 {
    font-size: 18px;
    margin: 0;
  }

  .header-text p {
    font-size: 12px;
    margin: 2px 0;
  }
}

</style>